/**
 * Demo finance for ?mock=1: a ledger, subscriptions, sync status and a report that agree with
 * the demo spend on the Spend page (same projects, same daily token spend). Changes made in
 * demo mode live only in this tab.
 */
import type {
  AccountBucket,
  CostEntry,
  CostEntryInput,
  CostsState,
  FinanceReport,
  FinanceSettings,
  FinanceSettingsInput,
  FinanceState,
  Id,
  ModelMixRow,
  PnlProject,
  PnlWeek,
  PrCost,
  Receipt,
  Subscription,
  SubscriptionInput,
  SyncKind,
  SyncStatus,
  VarianceRow,
} from '@orgus/protocol';
import { COST_CATEGORIES, DEFAULT_ACCOUNTS, UNKNOWN_ACCOUNT, zeroCategories } from '@orgus/protocol';
import { create } from 'zustand';
import { MOCK_IDS } from '../../lib/mock.ts';
import { dayOf, daysInclusive } from './model.ts';

const DAY = 86_400_000;

function noise(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return ((h >>> 0) % 1000) / 1000;
}

const parse = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12);
};
const addDays = (day: string, n: number) => {
  const d = parse(day);
  return dayOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12));
};
const weekOf = (day: string) => addDays(day, -((parse(day).getDay() + 6) % 7));

function demoSettings(): FinanceSettings {
  return {
    computeRates: [
      { machineId: MOCK_IDS.studio, hourlyRate: 0.42 },
      { machineId: MOCK_IDS.build, hourlyRate: 0.18 },
    ],
    keys: [
      { keyId: 'apikey_01Rj2N8SVvo6BePZj99NhmiT', label: 'Studio and laptop', machineIds: [MOCK_IDS.studio, MOCK_IDS.mbp] },
      { keyId: 'apikey_01ABC123DEF456GHI789JKL', label: 'Build box', machineIds: [MOCK_IDS.build] },
    ],
    repos: [{ repo: 'acme/billing-api', projectId: MOCK_IDS.billing }],
    githubOwner: 'acme',
    githubOwnerKind: 'org',
    accounts: { ...DEFAULT_ACCOUNTS },
    creditAccount: '2000 Accounts payable',
    varianceThreshold: 0.02,
  };
}

function demoLedger(now: number): { entries: CostEntry[]; subscriptions: Subscription[] } {
  const today = dayOf(new Date(now));
  const month = today.slice(0, 7);
  const base = { createdBy: 'owner', createdAt: now - 10 * DAY, updatedAt: now - 10 * DAY };
  const receipt: Receipt = { name: 'cloudflare-invoice.pdf', type: 'application/pdf', size: 48_211, uploadedAt: now - 9 * DAY };
  const entries: CostEntry[] = [
    { id: 'fe_demo_cf', category: 'platform', vendor: 'Cloudflare', description: 'Workers Paid and R2', amount: 25, from: `${month}-01`, to: addDays(`${month}-01`, 30), projectId: MOCK_IDS.orgus, source: 'invoice', invoiceNumber: 'CF-2210-4471', receipt, ...base },
    { id: 'fe_demo_fly', category: 'platform', vendor: 'Fly.io', description: 'Staging machines', amount: 38.4, from: `${month}-01`, to: addDays(`${month}-01`, 30), projectId: MOCK_IDS.billing, source: 'invoice', invoiceNumber: 'FLY-88213', ...base },
    { id: 'fe_demo_figma', category: 'other', vendor: 'Figma', description: 'Design seat for the console', amount: 15, from: addDays(today, -6), to: addDays(today, -6), projectId: null, source: 'manual', ...base },
    { id: 'fe_demo_domain', category: 'other', vendor: 'Namecheap', description: 'orgus.ai renewal', amount: 12.98, from: addDays(today, -12), to: addDays(today, -12), projectId: MOCK_IDS.orgus, source: 'manual', ...base },
    ...[1, 2, 3, 4, 5, 8, 9, 10].map((n) => ({
      id: `fe_demo_ci_${n}`,
      category: 'ci' as const,
      vendor: 'GitHub',
      description: `Actions, acme/billing-api, ${Math.round(150 + noise(`ci${n}`) * 300)} min`,
      amount: Math.round((1.2 + noise(`ci${n}`) * 2.4) * 100) / 100,
      from: addDays(today, -n),
      to: addDays(today, -n),
      projectId: MOCK_IDS.billing,
      source: 'github' as const,
      minutes: Math.round(150 + noise(`ci${n}`) * 300),
      createdBy: 'github',
      createdAt: now - 3_600_000,
      updatedAt: now - 3_600_000,
    })),
  ];
  const subscriptions: Subscription[] = [
    { id: 'fs_demo_max', vendor: 'Anthropic', plan: 'Claude Max', seats: 3, pricePerSeat: 200, every: 'month', start: addDays(today, -200), end: null, projectId: null, agentSeat: true, createdAt: now - 200 * DAY, updatedAt: now - 200 * DAY },
    { id: 'fs_demo_gh', vendor: 'GitHub', plan: 'Team', seats: 4, pricePerSeat: 4, every: 'month', start: addDays(today, -400), end: null, projectId: null, agentSeat: false, createdAt: now - 400 * DAY, updatedAt: now - 400 * DAY },
    { id: 'fs_demo_linear', vendor: 'Linear', plan: 'Standard, yearly', seats: 4, pricePerSeat: 96, every: 'year', start: addDays(today, -90), end: null, projectId: null, agentSeat: false, createdAt: now - 90 * DAY, updatedAt: now - 90 * DAY },
  ];
  return { entries, subscriptions };
}

function status(kind: SyncKind, now: number, configured = true): SyncStatus {
  const ran = new Date(now);
  const at = new Date(ran.getFullYear(), ran.getMonth(), ran.getDate(), 2, 37).getTime();
  const last = at > now ? at - DAY : at;
  const utcYesterday = new Date(now - DAY).toISOString().slice(0, 10);
  return {
    kind,
    configured,
    credentialFrom: configured ? 'settings' : null,
    hint: configured ? (kind === 'billing' ? 'x9Qe' : 'f31a') : null,
    lastRunAt: configured ? last : null,
    lastOkAt: configured ? last : null,
    running: false,
    error: null,
    coversFrom: configured ? new Date(now - 92 * DAY).toISOString().slice(0, 10) : null,
    coversTo: configured ? utcYesterday : null,
    nextRunAt: configured ? last + DAY : null,
  };
}

interface MockFinanceStore {
  state: FinanceState;
  saveEntry(input: CostEntryInput, id?: Id): CostEntry;
  removeEntry(id: Id): void;
  setReceipt(id: Id, receipt: Receipt | undefined): void;
  importRows(rows: CostEntry[]): void;
  saveSubscription(input: SubscriptionInput, id?: Id): void;
  removeSubscription(id: Id): void;
  saveSettings(input: FinanceSettingsInput): void;
  synced(kind: SyncKind): void;
}

function initial(now: number): FinanceState {
  return { settings: demoSettings(), ...demoLedger(now), sync: { billing: status('billing', now), github: status('github', now) }, version: 1, updatedAt: now };
}

export const useMockFinance = create<MockFinanceStore>()((set, get) => {
  const bump = (patch: Partial<FinanceState>) => set((s) => ({ state: { ...s.state, ...patch, version: s.state.version + 1, updatedAt: Date.now() } }));
  return {
    state: initial(Date.now()),
    saveEntry(input, id) {
      const now = Date.now();
      const prev = id ? get().state.entries.find((e) => e.id === id) : undefined;
      const e: CostEntry = {
        ...(prev ?? { id: `fe_demo_${now.toString(36)}`, createdBy: 'owner', createdAt: now }),
        category: input.category,
        vendor: input.vendor,
        description: input.description,
        amount: input.amount,
        from: input.from,
        to: input.to || input.from,
        projectId: input.projectId ?? null,
        source: input.source ?? prev?.source ?? 'manual',
        invoiceNumber: input.invoiceNumber,
        updatedAt: now,
      } as CostEntry;
      bump({ entries: prev ? get().state.entries.map((x) => (x.id === e.id ? e : x)) : [e, ...get().state.entries] });
      return e;
    },
    removeEntry(id) {
      bump({ entries: get().state.entries.filter((e) => e.id !== id) });
    },
    setReceipt(id, receipt) {
      bump({ entries: get().state.entries.map((e) => (e.id === id ? { ...e, receipt } : e)) });
    },
    importRows(rows) {
      bump({ entries: [...rows, ...get().state.entries] });
    },
    saveSubscription(input, id) {
      const now = Date.now();
      const prev = id ? get().state.subscriptions.find((s) => s.id === id) : undefined;
      const s: Subscription = {
        id: prev?.id ?? `fs_demo_${now.toString(36)}`,
        vendor: input.vendor,
        plan: input.plan,
        seats: input.seats,
        pricePerSeat: input.pricePerSeat,
        every: input.every,
        start: input.start,
        end: input.end ?? null,
        projectId: input.projectId ?? null,
        agentSeat: !!input.agentSeat,
        createdAt: prev?.createdAt ?? now,
        updatedAt: now,
      };
      bump({ subscriptions: prev ? get().state.subscriptions.map((x) => (x.id === s.id ? s : x)) : [...get().state.subscriptions, s] });
    },
    removeSubscription(id) {
      bump({ subscriptions: get().state.subscriptions.filter((s) => s.id !== id) });
    },
    saveSettings(input) {
      const { anthropicAdminKey, githubToken, ...rest } = input;
      const s = get().state;
      const sync = { ...s.sync };
      if (anthropicAdminKey !== undefined) sync.billing = anthropicAdminKey ? { ...status('billing', Date.now()), hint: anthropicAdminKey.slice(-4) } : status('billing', Date.now(), false);
      if (githubToken !== undefined) sync.github = githubToken ? { ...status('github', Date.now()), hint: githubToken.slice(-4) } : status('github', Date.now(), false);
      bump({ settings: { ...s.settings, ...rest, accounts: { ...s.settings.accounts, ...(rest.accounts ?? {}) } }, sync });
    },
    synced(kind) {
      const now = Date.now();
      const s = get().state;
      bump({ sync: { ...s.sync, [kind]: { ...s.sync[kind], lastRunAt: now, lastOkAt: now, error: null } } });
    },
  };
});

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

function variance(key: string, label: string, telemetry: number, billing: number, threshold: number, extra: Partial<VarianceRow> = {}): VarianceRow {
  const v = telemetry - billing;
  const pct = billing > 0 ? v / billing : null;
  return { key, label, telemetry, billing, variance: v, variancePct: pct, flagged: billing > 0 ? Math.abs(v) / billing > threshold : telemetry > 0.01, ...extra };
}

/** A report shaped like the hub's, from the demo spend and the demo ledger. */
export function buildMockReport(o: { costs: CostsState; finance: FinanceState; from: string; to: string; projectId: Id | null; account?: string | null; projects: Record<Id, { name: string }>; now: number }): FinanceReport {
  const { costs, finance, from, to, projectId } = o;
  // One account (ADR 0067): its share of token spend, and nothing else (seats and other costs belong to no account).
  const account = o.account ?? null;
  const accountKey = account === null ? null : account === UNKNOWN_ACCOUNT ? '' : account;
  const accountTotal = (costs.byAccount ?? []).reduce((a, b) => a + b.cost, 0) || 1;
  const accountShare = accountKey === null ? 1 : ((costs.byAccount ?? []).find((b) => b.key === accountKey)?.cost ?? 0) / accountTotal;
  const byDay = new Map(costs.byDay.map((b) => [b.key, b.cost]));
  const tokenOn = (day: string) => (byDay.get(day) ?? ([0, 6].includes(parse(day).getDay()) ? 4 : 14) + noise(day) * 12) * accountShare;
  const projectShares = costs.byProject.filter((p) => p.key).map((p) => ({ id: p.key, share: p.cost }));
  const totalShare = projectShares.reduce((a, p) => a + p.share, 0) || 1;
  const inScope = (pid: Id | null) => projectId === null || pid === projectId;
  const scopeShare = projectId === null ? 1 : (projectShares.find((p) => p.id === projectId)?.share ?? 0) / totalShare;

  const weeks = new Map<string, PnlWeek>();
  const projects = new Map<string, PnlProject>();
  const byCategory = zeroCategories();
  const add = (day: string, cat: (typeof COST_CATEGORIES)[number], pid: Id | null, amount: number) => {
    if (!inScope(pid) || amount <= 0) return;
    const w = weekOf(day);
    const wk = weeks.get(w) ?? { week: w, byCategory: zeroCategories(), total: 0, merged: 0 };
    wk.byCategory[cat] += amount;
    wk.total += amount;
    weeks.set(w, wk);
    byCategory[cat] += amount;
    const pk = pid ?? '';
    const p = projects.get(pk) ?? { projectId: pk, label: pk ? (o.projects[pk]?.name ?? pk) : 'No project', byCategory: zeroCategories(), total: 0 };
    p.byCategory[cat] += amount;
    p.total += amount;
    projects.set(pk, p);
  };
  const split = (day: string, cat: (typeof COST_CATEGORIES)[number], amount: number) => {
    for (const p of projectShares) add(day, cat, p.id, (amount * p.share) / totalShare);
  };

  const nDays = daysInclusive(from, to);
  for (let i = 0; i < nDays; i++) {
    const day = addDays(from, i);
    split(day, 'tokens', tokenOn(day));
    if (accountKey !== null) continue;
    split(day, 'compute', (18 * 0.42 + 24 * 0.18) * (0.8 + noise(`c${day}`) * 0.4));
    for (const s of finance.subscriptions) {
      if (day < s.start || (s.end && day > s.end)) continue;
      const d = parse(day);
      const per = s.every === 'month' ? new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() : 365;
      const amt = (s.seats * s.pricePerSeat) / per;
      if (s.projectId) add(day, 'seats', s.projectId, amt);
      else split(day, 'seats', amt);
    }
    for (const e of finance.entries) {
      if (day < e.from || day > e.to) continue;
      const amt = e.amount / daysInclusive(e.from, e.to);
      if (e.projectId) add(day, e.category, e.projectId, amt);
      else split(day, e.category, amt);
    }
  }
  if (projectId === null && accountKey === null) add(to, 'tokens', null, 3.12);
  const weekList = [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week));
  for (const w of weekList) w.merged = Math.round((6 + noise(`m${w.week}`) * 9) * (projectId === null ? 1 : Math.max(0.15, scopeShare)));
  const total = COST_CATEGORIES.reduce((a, c) => a + byCategory[c], 0);
  const merged = weekList.reduce((a, w) => a + w.merged, 0);
  const activeHours = (byCategory.tokens / 9.5) * (0.9 + noise('ah') * 0.2);

  const titles = ['Ledger pagination', 'Invoice webhooks', 'Retry budget on 429', 'Cache warmup', 'Seat sync job', 'Plan picker copy', 'Faster CI shards', 'Drop legacy exporter'];
  const prs: PrCost[] = titles
    .map((title, i) => {
      const state: PrCost['state'] = i === 3 ? 'reverted' : i === 6 ? 'abandoned' : 'merged';
      const at = o.now - (i + 1) * 1.7 * DAY;
      return { projectId: MOCK_IDS.billing, repo: 'acme/billing-api', number: 412 - i, title, state, mergedAt: state === 'abandoned' ? null : at, closedAt: at, branch: title.toLowerCase().replace(/[^a-z0-9]+/g, '-'), cost: 3 + noise(title) * 26, sessions: 1 + Math.round(noise(`s${title}`) * 3) };
    })
    .filter((p) => inScope(p.projectId))
    .sort((a, b) => b.cost - a.cost);
  const rework = prs.filter((p) => p.state !== 'merged').reduce((a, p) => a + p.cost, 0);
  const attributed = prs.reduce((a, p) => a + p.cost, 0);
  const tm = costs.tokensMonth;
  const allInput = tm.input + tm.cacheRead + tm.cacheWrite;

  // Reconciliation: the bill a little under telemetry, one day over the line.
  const threshold = finance.settings.varianceThreshold;
  const billed = finance.sync.billing.configured;
  const coversTo = finance.sync.billing.coversTo ?? '';
  const recDays: VarianceRow[] = [];
  for (let i = 0; i < nDays; i++) {
    const day = addDays(from, i);
    const tel = (tokenOn(day) / Math.max(accountShare, 1e-9)) * scopeShare;
    if (!billed || day > coversTo) {
      recDays.push({ ...variance(day, day, tel, 0, threshold), flagged: false, missing: true, variance: 0, variancePct: null });
      continue;
    }
    const drift = noise(`r${day}`);
    const bill = tel * (drift > 0.93 ? 0.9 : 1 - 0.012 + drift * 0.02);
    recDays.push(variance(day, day, tel, bill, threshold));
  }
  const counted = recDays.filter((d) => !d.missing);
  const telemetry = counted.reduce((a, d) => a + d.telemetry, 0);
  const billing = counted.reduce((a, d) => a + d.billing, 0);
  const modelTotal = costs.byModel.reduce((a, m) => a + m.cost, 0) || 1;
  const byModel = costs.byModel.map((m, i) => variance(m.key, m.label, (telemetry * m.cost) / modelTotal, ((billing * m.cost) / modelTotal) * (i === 0 ? 1 : 1.004), threshold));
  const byKey = [
    variance('apikey_01Rj2N8SVvo6BePZj99NhmiT', 'Studio and laptop', telemetry * 0.71, billing * 0.7, threshold, { billingTokens: Math.round(billing * 0.7 * 260_000) }),
    variance('apikey_01ABC123DEF456GHI789JKL', 'Build box', telemetry * 0.29, billing * 0.295, threshold, { billingTokens: Math.round(billing * 0.295 * 260_000) }),
    { ...variance('', 'Console, no key', 0, billing * 0.005, threshold), flagged: false, unmapped: true, billingTokens: Math.round(billing * 0.005 * 260_000) },
  ];

  const rates = new Map(costs.pricing.prices.map((p) => [p.match, p]));
  const mix: ModelMixRow[] = costs.byModel.map((m) => {
    const p = rates.get(m.key) ?? [...rates.values()].find((x) => m.key.startsWith(x.match)) ?? null;
    const share = (byCategory.tokens * m.cost) / modelTotal;
    const blended = p ? (p.input * 0.25 + p.output * 0.15 + p.cacheRead * 0.6) : 1;
    const tokens = (share / Math.max(blended, 1e-9)) * 1e6;
    return {
      model: m.key,
      label: m.label,
      cost: share,
      tokens: { input: tokens * 0.25, output: tokens * 0.15, cacheRead: tokens * 0.6, cacheWrite: 0, cacheWrite1h: 0 },
      rates: p ? { input: p.input, output: p.output, cacheRead: p.cacheRead, cacheWrite: p.cacheWrite, cacheWrite1h: p.cacheWrite1h } : null,
      lightShare: 0.18 + noise(m.key) * 0.3,
    };
  });

  // Token spend of the period by account, in the same shares as this month's.
  const periodTokens = byCategory.tokens / Math.max(accountShare, 1e-9);
  const accounts: AccountBucket[] = (costs.byAccount ?? []).map((b) => ({ ...b, cost: (periodTokens * b.cost) / accountTotal, tokens: Math.round((b.tokens * periodTokens) / Math.max(costs.month, 1e-9)) }));

  return {
    from,
    to,
    projectId,
    accountKey,
    accounts,
    billing: costs.billing,
    pricingVersion: costs.pricing.version,
    weeks: weekList,
    projects: [...projects.values()].sort((a, b) => b.total - a.total),
    byCategory,
    total,
    kpis: {
      total,
      tokens: byCategory.tokens,
      mergedPrs: merged,
      costPerMergedPr: merged ? total / merged : null,
      tokenCostPerMergedPr: merged ? byCategory.tokens / merged : null,
      activeHours,
      costPerActiveHour: activeHours > 0 ? byCategory.tokens / activeHours : null,
      missions: [
        { missionId: 'mi_demo_1', title: 'Usage-based invoices', state: 'done', cost: 84.2 * scopeShare, sessions: 6 },
        { missionId: 'mi_demo_2', title: 'Landing queue autoscaling', state: 'running', cost: 41.7 * scopeShare, sessions: 3 },
        { missionId: 'mi_demo_3', title: 'Docs search', state: 'done', cost: 22.9 * scopeShare, sessions: 2 },
      ].filter((m) => m.cost > 0.5),
      costPerMission: 53.55 * scopeShare,
      cacheHitRatio: allInput > 0 ? tm.cacheRead / allInput : null,
      cacheSavings: (tm.cacheRead * 3.6) / 1e6,
      reworkRate: attributed > 0 ? rework / attributed : null,
      reworkCost: rework,
      attributedPrCost: attributed,
      approvalWait: { hours: 6.4 * scopeShare + 0.2, episodes: Math.round(48 * scopeShare) + 1, medianMinutes: 2.6, p90Minutes: 14 },
      sampledHours: nDays * 22,
    },
    prs,
    reconciliation: {
      status: billed ? 'ready' : costs.billing === 'subscription' ? 'subscription' : 'none',
      byDay: recDays,
      byModel,
      byKey,
      telemetry,
      billing,
      billingOther: billed ? 1.84 : 0,
      variance: telemetry - billing,
      variancePct: billing > 0 ? (telemetry - billing) / billing : null,
      threshold,
      missingDays: recDays.filter((d) => d.missing).map((d) => d.key),
    },
    modelMix: mix,
    unallocated: projectId === null ? [{ category: 'tokens', amount: 3.12, why: 'Sessions ran outside any project folder, so their tokens have no project. Start sessions inside a repository.' }] : [],
    generatedAt: o.now,
  };
}
